import { defineStore } from 'pinia'

import { apiClientV2, Context } from '@/api/ApiClientV2'
import {
  KnockautModule,
  KnockautModuleInstallation,
  KnockautModuleOrder,
} from '@/apps/brelag/common/models/knockautModule'
import { SymconModuleBranchInfo } from '@/apps/brelag/mandator-user/project-editor/knockaut/interfaces'
import { KnockautHandler } from '@/apps/brelag/mandator-user/project-editor/knockaut/knockautHandler'
import { SymconLibrary } from '@/apps/brelag/mandator-user/project-editor/knockaut/knockautModuleHandler'

export interface LibraryUpdateResponse extends SymconLibrary {
  UpdateAvailable: boolean
  ModuleName: string // The ModuleName is taken from the GitHub URL (the last part)
}

export const useKnockautStore = defineStore('knockaut', {
  // arrow function recommended for full type inference
  state: () => {
    return {
      initialized: false,
      knockautHandler: KnockautHandler.instance as KnockautHandler,
      moduleUpdates: [] as LibraryUpdateResponse[],
      moduleInstallations: [] as KnockautModuleInstallation[],
      allStylerModules: [] as KnockautModule[],
      branchInfos: {} as { [key: string]: SymconModuleBranchInfo },
      moduleOrders: [] as KnockautModuleOrder[],
      moduleUpdateAvailable: {} as Record<string, boolean>,
    }
  },
  actions: {
    async initialize() {
      try {
        await this.getStylerModules()
        await this.getModuleOrders()
        await this.getModuleInstallations()
        this.initialized = true
      } catch (error) {
        this.moduleInstallations = []
        this.allStylerModules = []
        this.branchInfos = {}
        this.moduleOrders = []
        this.moduleUpdates = []
        this.initialized = false
        throw error
      }
    },
    async syncModules() {
      await apiClientV2.customPost(
        `brelag/knockaut-project/sync-knockaut-installations`,
        {
          knockaut_project: KnockautHandler.instance.project.id,
        }
      )
    },
    async getStylerModules() {
      const context: Context = {
        filter: {},
        pagination: {},
      }
      this.allStylerModules = await apiClientV2.getListItems<KnockautModule>(
        KnockautModule,
        context
      )
      try {
        const branchInfos =
          await KnockautHandler.instance.getModuleBranchInfos()
        this.branchInfos = branchInfos
      } catch (error) {
        // Possible that controller does not support branch changing yet
      }
    },
    async getModuleOrders() {
      const context: Context = {
        filter: {
          knockaut_project: KnockautHandler.instance.project.id,
        },
        pagination: {},
      }
      this.moduleOrders = await apiClientV2.getListItems<KnockautModuleOrder>(
        KnockautModuleOrder,
        context
      )
    },
    async getModuleInstallations() {
      const context: Context = {
        filter: {
          knockaut_project: KnockautHandler.instance.project.id,
        },
        pagination: {},
      }
      this.moduleUpdateAvailable = {}
      this.moduleInstallations =
        await apiClientV2.getListItems<KnockautModuleInstallation>(
          KnockautModuleInstallation,
          context
        )
    },
    async checkForUpdates() {
      this.moduleUpdates = await apiClientV2.customPost(
        `brelag/knockaut-project/check-for-module-updates`,
        {
          id: KnockautHandler.instance.project.id,
        }
      )

      const moduleUpdateAvailable = {}
      this.moduleUpdates.modules.forEach(
        (moduleUpdate: LibraryUpdateResponse) => {
          moduleUpdateAvailable[moduleUpdate.LibraryID] =
            moduleUpdate.UpdateAvailable
        }
      )
      this.moduleUpdateAvailable = moduleUpdateAvailable
    },
    async createModuleOrder(module: KnockautModule) {
      const response = await apiClientV2.create(KnockautModuleOrder, {
        module: module.id,
        project: KnockautHandler.instance.project.id,
        organisation: KnockautHandler.instance.parentProject.organisation,
      })
      await this.getModuleInstallations()
      const order = response as KnockautModuleOrder
      this.moduleOrders.push(order)
      return order
    },
    async updateModule(moduleInstallation: KnockautModuleInstallation) {
      await apiClientV2.customPost(
        `brelag/knockaut-module-installation/${moduleInstallation.id}/update-module`
      )
      await this.getModuleInstallations()
    },
    async installModule(moduleInstallation: KnockautModuleInstallation) {
      await apiClientV2.customPost(
        `brelag/knockaut-module-installation/${moduleInstallation.id}/install-module`
      )
      await KnockautHandler.instance.refreshTree(true)
      await this.getModuleInstallations()
    },
    async uninstallModule(moduleInstallation: KnockautModuleInstallation) {
      await apiClientV2.customPost(
        `brelag/knockaut-module-installation/${moduleInstallation.id}/uninstall-module`
      )
      await KnockautHandler.instance.refreshTree(true)
      await this.getModuleInstallations()
    },
  },
  getters: {
    hiddenKnockautModules(state): KnockautModule[] {
      return state.allStylerModules.filter((module: KnockautModule) => {
        return module.is_internal
      })
    },
    visibleKnockautModules(state): KnockautModule[] {
      return state.allStylerModules.filter(
        (module: KnockautModule) => !KnockautModule.isSubscription(module)
      )
    },
    baseKnockautModules(state): KnockautModule[] {
      const baseModuleIDs = [
        '{4BE0B0C6-01AB-220E-9993-A2FB5232CD7C}', // Analytics
        '{7E2FBCEA-D9A4-4710-B56D-860748B01B17}', // DominoSwiss
      ]
      return state.allStylerModules.filter((module: KnockautModule) =>
        baseModuleIDs.some((baseID) => baseID === module.guid)
      )
    },
    modulesAvailableForPurchase(state) {
      return this.visibleKnockautModules.filter((module: KnockautModule) => {
        return state.moduleOrders.every((order: KnockautModuleOrder) => {
          return order.module !== module.id
        })
      })
    },
    purchasedModules(state) {
      return this.visibleKnockautModules.filter((module: KnockautModule) => {
        return state.moduleOrders.some((order: KnockautModuleOrder) => {
          return order.module === module.id
        })
      })
    },
  },
})
